import React, { isValidElement, memo } from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import Link from '@material-ui/core/Link';
import styled from 'styled-components';
import { Typography } from '@utmost.co/utmost-design-system';
import { useSmartFormContext } from '../../forms/utils/useSmartFormContext';
import { PermissionHelper } from '../common/PermissionHelper';
import { ComponentPropBase } from '../../types';
import { FieldPermission } from '../../forms/controllers/SmartformContext';
import { InfoText } from '../InfoText/InfoText';

export type ReadOnlyProps = {
  permissionsId?: string;
  label: string | React.ReactNode;
  infoText?: string;
  value?: React.ReactNode;
  icon?: React.ReactNode;
  fullWidth?: boolean;
  fieldControl?: boolean;
  /* @deprecated */
  multiline?: boolean;
  type?: ComponentPropBase['type'];
  className?: string;
  error?: string | React.ReactNode;
  permission?: FieldPermission;
  disablePermissionDebugger?: boolean;
};

export const ReadOnly: React.FC<React.PropsWithChildren<ReadOnlyProps>> = memo(
  ({
    label,
    value,
    icon,
    fullWidth = true,
    permissionsId,
    infoText,
    type,
    fieldControl,
    className,
    error,
    permission,
    disablePermissionDebugger,
    multiline
  }) => {
    const { getFieldPermissions, permissionDebugger } = useSmartFormContext();
    const dataTest = !!fieldControl && !!permissionsId ? permissionsId : undefined;
    const isEmpty = !value || (typeof value === 'string' && value === '');
    const isComponent = isReactComponent(value) || isReactArrayComponent(value);

    if (permissionsId) {
      const idPermissions = getFieldPermissions(permissionsId);
      if (permission && idPermissions) {
        permission.READ = idPermissions.READ;
        permission.WRITE = idPermissions.WRITE;
      }
    }

    return (
      <FormControl
        className={className}
        fullWidth={fullWidth}
        disabled
        data-test={dataTest ? `${dataTest}-field-control` : undefined}
      >
        <FormLabel htmlFor={permissionsId} id={`label-${permissionsId}`}>
          {infoText ? <InfoText label={label} required={false} tooltipText={infoText} /> : label}
        </FormLabel>
        {type === 'url' && typeof value === 'string' ? (
          <StyledLink
            href={getHref(value)}
            data-test={dataTest ? `${dataTest}-field` : 'read-only-value'}
            data-test-readonly-value={value}
          >
            {getHref(value)}
          </StyledLink>
        ) : isComponent ? (
          value
        ) : (
          <StyledPreview
            id={permissionsId}
            aria-labelledby={`label-${permissionsId}`}
            data-test={dataTest ? `${dataTest}-field` : 'read-only-value'}
            data-test-readonly-value={!isEmpty ? value : '-'}
            role="textbox"
          >
            {!!icon && <StyledIconContainer>{icon}</StyledIconContainer>}
            {!isEmpty ? getText(value) : '-'}
          </StyledPreview>
        )}

        {error && <StyledError variant="small">{error}</StyledError>}
        {permissionDebugger && !disablePermissionDebugger && <PermissionHelper permission={permission} />}
      </FormControl>
    );
  }
);

export const getHref = (value: string = '') => {
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  return value.length > 0 ? `//${value}` : '-';
};

const StyledError = styled(Typography)`
  padding: 0.25rem 0;
  color: ${({ theme }) => theme.colorAlert};
`;

const StyledLink = styled(Link)`
  // some long links can be a pain in the ass and not wrap to the next line this fixes that.
  // https://css-tricks.com/snippets/css/prevent-long-urls-from-breaking-out-of-container/
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;

  /* Adds a hyphen where the word breaks, if supported (No Blink) */
  -ms-hyphens: auto;
  -moz-hyphens: auto;
  -webkit-hyphens: auto;
  hyphens: auto;
`;

function isReactComponent(value: unknown) {
  const isEmpty = !value || (typeof value === 'string' && value === '');
  return !isEmpty && !['number', 'string'].includes(typeof value) && isValidElement(value);
}

function isReactArrayComponent(value: unknown) {
  return Array.isArray(value) && value.every(isReactComponent);
}

function getText(value: React.ReactNode) {
  if (['string', 'number'].includes(typeof value)) {
    return value;
  }
  if (isReactArrayComponent(value) || isReactComponent(value)) {
    return value;
  }
  return Array.isArray(value) ? value.join(',') : value;
}

const StyledPreview = styled.div`
    padding: 6px 16px 7px 0;
    font-size: ${({ theme }) => theme.font.sizeSmall};
    line-height: 16.6px;
    white-space-collapse: preserve;
    letter-spacing:  0.13132px;
    word-break: break-word;
`;

const StyledIconContainer = styled.div`
    display: inline-block;
    vertical-align: middle;
    margin-right: ${({ theme }) => theme.spacingSize.s}
`;
